import { useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
import { EMPTY_LIST } from "src/common/constants";
import LicenseManagementService from "src/modules/3rd-party-management/apis/admin/LicenseManagementService";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { useState } from "react";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';

const CreditHistory = ({ t }) => {
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 50,
        sortField: null,
        sortOrder: null
    });

    const dateUtils = new DateUtils();
        
    const {
        data: historyData,
        isFetching: isFetchingHistory,
        isFetched: historyIsFetched,
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-organization-license-history',
        ],
        queryFn: async () => {
            const service = LicenseManagementService.getInstance();

            return await service.fetchHistory({});
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to fetch history logs.'), {
                type: 'error'
            });
        },
    });

    const tableColumns = [
        {
            dataField: "id",
            text: t("ID"),
            sort: true,
            style: {
                width: '80px'
            }
        },
        {
            dataField: "action",
            text: t("Action"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cellContent) => {
                switch(cellContent) {
                    case 'register_plot':
                        return t('Plot Registration');
                    default:
                        return cellContent;
                }
            }
        },
        {
            dataField: "area",
            text: `${t("Used {{creditsLabel}}", {creditsLabel: "Credits"})} (ha)`,
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cellContent) => {
                return parseFloat(cellContent).toLocaleString();
            }
        },
        {
            dataField: "createdAt",
            text: t("Date"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cellContent) => {
                return dateUtils.convertDateToDate(cellContent,  INTERNATIONAL_DATE_FORMAT)
            }
        },
    ];

    const NoDataIndication = () => {
        if (historyIsFetched && !historyData?.logs?.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p style={{
                        textAlign: "center",
                        marginBottom: 0
                    }}>
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            )
        }

        return <></>
    }

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch(type){
            case 'pagination':
                    setFilters({
                        ...filters,
                        pageIndex   :   page || 1,
                        pageSize    :   sizePerPage
                    });
                break;
            case 'sort':
                    setFilters({
                        ...filters,
                        sortField   :   sortField,
                        sortOrder   :   sortOrder.toUpperCase()
                    })
                break;
            default:
                return false;
        }
    }

    return (
        <div className="credit-history-tab">
            <Row>
                <Col sm='12'>
                    <Card>
                        <CardBody>
                            <PaginationProvider
                                pagination={
                                    paginationFactory({
                                        custom: true,
                                        page: filters.pageIndex,
                                        sizePerPage: filters.pageSize,
                                        totalSize: historyData?.logs.length || 0,
                                        withFirstAndLast: true,
                                        alwaysShowAllBtns: true,
                                        prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                        nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                        prePageTitle: t('Pre page'),
                                        firstPageTitle: t('First page'),
                                        lastPageTitle: t('Last page'),
                                        showTotal: false,
                                        paginationTotalRenderer: (from, to, size) => {
                                            return (
                                                <span className="react-bootstrap-table-pagination-total">
                                                    {`${t('Total Items')} ${size}`}
                                                </span>
                                            )
                                        },
                                        hideSizePerPage: false,
                                        sizePerPageList: [
                                            {
                                                text: '25',
                                                value: 25
                                            },
                                            {
                                                text: '50',
                                                value: 50
                                            }
                                        ]
                                    })
                                }>
                                {({
                                    paginationProps,
                                    paginationTableProps
                                }) => (
                                    <>
                                        <Row>
                                            <Col sm="12">
                                                <BootstrapTable
                                                    loading={isFetchingHistory}
                                                    remote={{
                                                        pagination: true,
                                                        filter: false,
                                                        sort: true,
                                                        cellEdit: false,
                                                        search: false
                                                    }}
                                                    overlay={
                                                        overlayFactory({
                                                            spinner: <Spinner animation="border" variant="primary" size="md" />,
                                                            text: `${t("Loading")}...`
                                                        })
                                                    }
                                                    keyField={"id"}
                                                    responsive
                                                    bordered={false}
                                                    data={historyData?.logs || []}
                                                    striped={true}
                                                    columns={tableColumns}
                                                    onTableChange={handleTableChange}
                                                    wrapperClasses="table-responsive"
                                                    classes={"table tpdd-table"}
                                                    headerWrapperClasses={"thead-light"}
                                                    style={{
                                                        overflowX: "auto",
                                                    }}
                                                    noDataIndication={() => <NoDataIndication />}
                                                    {...paginationTableProps}
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col sm="12">
                                                <div className="tpdd-pagination-style-1">
                                                    <PaginationTotalStandalone
                                                        {...paginationProps}
                                                    />

                                                    <div>
                                                        <PaginationListStandalone
                                                            {...paginationProps}
                                                        />

                                                        <SizePerPageDropdownStandalone
                                                            {...paginationProps}
                                                        />
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </PaginationProvider>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default withNamespaces()(
    CreditHistory
);